import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { MdNavigateBefore, MdNavigateNext, MdFirstPage, MdLastPage } from 'react-icons/md';

const CustomPagination = ({
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,
    loading = false
}) => {
    console.log('🎯 CustomPagination props:', {
        currentPage,
        totalItems,
        itemsPerPage,
        loading
    });

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    console.log('🧮 Pagination calculations:', {
        totalPages,
        startItem,
        endItem,
        'currentPage - 1': currentPage - 1,
        '(currentPage - 1) * itemsPerPage': (currentPage - 1) * itemsPerPage,
        'Math.min calculation': Math.min(currentPage * itemsPerPage, totalItems)
    });

    const handlePageChange = (newPage) => {
        console.log('🔄 CustomPagination handlePageChange called with:', newPage);
        console.log('🔄 Current page before change:', currentPage);

        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !loading) {
            console.log('✅ Calling onPageChange with page:', newPage);
            onPageChange(newPage);
        } else {
            console.log('❌ Page change blocked:', {
                'newPage >= 1': newPage >= 1,
                'newPage <= totalPages': newPage <= totalPages,
                'newPage !== currentPage': newPage !== currentPage,
                'not loading': !loading
            });
        }
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        console.log('🔄 CustomPagination handleItemsPerPageChange called with:', newItemsPerPage);
        if (onItemsPerPageChange) {
            onItemsPerPageChange(newItemsPerPage);
        }
    };

    // Generate page numbers for pagination display
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        // Adjust startPage if we're near the end
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    if (totalItems === 0) {
        return (
            <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2 bg-light rounded">
                <div className="text-muted">
                    <small>No items to display</small>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2 bg-light rounded">
            <div className="d-flex align-items-center gap-3">
                {/* Items per page selector */}
                <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">Show:</small>
                    <Form.Select
                        size="sm"
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                        disabled={loading}
                        style={{ width: '80px' }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Form.Select>
                    <small className="text-muted">per page</small>
                </div>

                {/* Items range display */}
                <div>
                    <small className="text-muted">
                        Showing {startItem}-{endItem} of {totalItems} items
                        {loading && <span className="text-primary ms-2">Loading...</span>}
                    </small>
                </div>
            </div>

            {/* Pagination controls */}
            <div className="d-flex align-items-center gap-1">
                {/* First page button */}
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1 || loading}
                    onClick={() => handlePageChange(1)}
                    className="d-flex align-items-center"
                >
                    <MdFirstPage size={16} />
                </Button>

                {/* Previous page button */}
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="d-flex align-items-center"
                >
                    <MdNavigateBefore size={16} />
                </Button>

                {/* Page numbers */}
                {getPageNumbers().map((pageNum) => (
                    <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "primary" : "outline-secondary"}
                        size="sm"
                        disabled={loading}
                        onClick={() => handlePageChange(pageNum)}
                        style={{ minWidth: '32px' }}
                    >
                        {pageNum}
                    </Button>
                ))}

                {/* Next page button */}
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages || loading}
                    onClick={() => {
                        console.log('🚀 NEXT BUTTON CLICKED');
                        console.log('📍 Current page:', currentPage);
                        console.log('📍 Will navigate to page:', currentPage + 1);
                        handlePageChange(currentPage + 1);
                    }}
                    className="d-flex align-items-center"
                >
                    <MdNavigateNext size={16} />
                </Button>

                {/* Last page button */}
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages || loading}
                    onClick={() => handlePageChange(totalPages)}
                    className="d-flex align-items-center"
                >
                    <MdLastPage size={16} />
                </Button>
            </div>
        </div>
    );
};

export default CustomPagination;